/**
 * Harici yedeğin alarm seviyesi (ADR 0041 §8). Kaptanın zili, Settings ▸ Backup kartı, ofis
 * panosu ve filo ekranı aynı kuralı gösterir (ADR 0028 #32 madde 2). Saf fonksiyon: saat
 * parametreyle gelir.
 */

/** Hedefin durumu (ADR 0041 §2). */
export const BACKUP_TARGET_STATES = [
  'ok',
  'not_configured',
  'missing',
  'locked',
  'full',
  'read_only',
  'foreign',
  'error',
] as const;
export type BackupTargetState = (typeof BACKUP_TARGET_STATES)[number];

/** Hedefin BitLocker durumu (ADR 0041 §2): algılanamıyorsa `unknown`. */
export const BACKUP_ENCRYPTION_STATES = ['yes', 'no', 'unknown'] as const;
export type BackupEncryption = (typeof BACKUP_ENCRYPTION_STATES)[number];

export const BACKUP_ALARM_LEVELS = ['ok', 'warning', 'critical'] as const;
export type BackupAlarmLevel = (typeof BACKUP_ALARM_LEVELS)[number];

export interface BackupAlarmItem {
  code: string;
  level: Exclude<BackupAlarmLevel, 'ok'>;
}

export interface BackupAlarm {
  level: BackupAlarmLevel;
  items: BackupAlarmItem[];
}

export interface BackupAlarmInput {
  now: Date;
  targetState: BackupTargetState;
  /** Hedefin bu duruma geçtiği an (UTC ISO-8601). */
  targetStateSince: string | null;
  encrypted: BackupEncryption | null;
  lastSnapshotAt: string | null;
  /** Yedeğe girmemiş en eski gemi dosyasının kayıt zamanı; hepsi yedekteyse `null`. */
  oldestUnbackedShipFileAt: string | null;
  companyFilesMissing: number;
  /** Son haftalık doğrulamanın sonucu; hiç yapılmadıysa `null`. */
  lastVerifyOk: boolean | null;
  unresolvedConflicts: number;
  /** Servisin bulduğu ek sorunlar (paket dosyası çakışması, bozuk yerel nesne). */
  extra?: readonly BackupAlarmItem[];
}

const HOUR = 3_600_000;
/** Hedef yokluğu ve yedeksiz gemi dosyası bu süreden sonra kritik (ADR 0041 §8). */
export const BACKUP_CRITICAL_AFTER_MS = 24 * HOUR;
export const SNAPSHOT_WARNING_AFTER_MS = 36 * HOUR;
export const SNAPSHOT_CRITICAL_AFTER_MS = 72 * HOUR;

const UNAVAILABLE: readonly BackupTargetState[] = [
  'missing',
  'locked',
  'full',
  'read_only',
  'foreign',
  'error',
];

const RANK: Readonly<Record<BackupAlarmLevel, number>> = { ok: 0, warning: 1, critical: 2 };

/**
 * ADR 0041 §8 tablosu:
 *
 * | Durum | Seviye |
 * |---|---|
 * | Hedef tanımlı değil | Uyarı, kalıcı (mimari belge §5.0) |
 * | Hedef yok, kilitli, dolu, salt okunur ya da başka geminin | Uyarı; 24 saati geçerse kritik |
 * | Gemi dosyası 24 saatten uzun yedeksiz | Kritik (ADR 0028 #32 madde 2) |
 * | Son başarılı görüntü 36 saatten eski | Uyarı; 72 saatten eskiyse kritik |
 * | Şirket dosyaları yedekte eksik | Uyarı |
 * | Haftalık doğrulama başarısız | Kritik |
 * | Hedefte BitLocker kapalı ya da bilinmiyor | Uyarı |
 * | Çözülmemiş geri yükleme çakışması | Uyarı |
 */
export function backupAlarm(input: BackupAlarmInput): BackupAlarm {
  const now = input.now.getTime();
  const age = (at: string | null) =>
    at === null ? Number.POSITIVE_INFINITY : now - Date.parse(at);
  const items: BackupAlarmItem[] = [];
  if (input.targetState === 'not_configured') {
    items.push({ code: 'TARGET_NOT_CONFIGURED', level: 'warning' });
  } else if (UNAVAILABLE.includes(input.targetState)) {
    const level = age(input.targetStateSince) > BACKUP_CRITICAL_AFTER_MS ? 'critical' : 'warning';
    items.push({ code: 'TARGET_UNAVAILABLE', level });
  }
  if (
    input.oldestUnbackedShipFileAt !== null &&
    age(input.oldestUnbackedShipFileAt) > BACKUP_CRITICAL_AFTER_MS
  ) {
    items.push({ code: 'SHIP_FILES_UNBACKED', level: 'critical' });
  }
  const snapshotAge = age(input.lastSnapshotAt);
  if (snapshotAge > SNAPSHOT_CRITICAL_AFTER_MS && input.lastSnapshotAt !== null) {
    items.push({ code: 'SNAPSHOT_STALE', level: 'critical' });
  } else if (snapshotAge > SNAPSHOT_WARNING_AFTER_MS) {
    items.push({ code: 'SNAPSHOT_STALE', level: 'warning' });
  }
  if (input.companyFilesMissing > 0)
    items.push({ code: 'COMPANY_FILES_MISSING', level: 'warning' });
  if (input.lastVerifyOk === false) items.push({ code: 'VERIFY_FAILED', level: 'critical' });
  if (input.targetState === 'ok' && input.encrypted !== 'yes') {
    items.push({ code: 'TARGET_NOT_ENCRYPTED', level: 'warning' });
  }
  if (input.unresolvedConflicts > 0) items.push({ code: 'RESTORE_CONFLICTS', level: 'warning' });
  items.push(...(input.extra ?? []));
  const level = items.reduce<BackupAlarmLevel>(
    (worst, item) => (RANK[item.level] > RANK[worst] ? item.level : worst),
    'ok',
  );
  return { level, items };
}
